import { Fragment, memo, useCallback } from 'react';
import { Platform, StyleSheet, TouchableOpacity, View, ViewProps } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { useMutation } from '@apollo/client';

import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Text, Divider, ArrowRightIcon, spacing, useConfirm } from '@pletly/ui';
import { getName } from '@pletly/shared';
import { RootStackParamList } from '%navigation/types';
import { UserInfo } from '%components';
import DeleteIcon from '%assets/icons/delete.svg';
import { useLoggedUser } from '%context/LoggedUser';
import { DISCONNECT_CAREGIVER } from './disconnectCareGiver.graphql';
import {
	DisconnectCareGiver,
	DisconnectCareGiverVariables,
} from './__generated__/disconnectCareGiver';
import { GetUserInfoDocument, GetUserInfoQuery } from '../__generated__/userInfo.generated';

export const CareReceiverConnections = memo(
	({ careReceiverInfo, style, enableDeleteCareGivers = false, ...props }: Props) => {
		const { t } = useTranslation();
		const { push } = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
		const { confirm } = useConfirm();
		const [disconnectCareGiver] = useMutation<DisconnectCareGiver, DisconnectCareGiverVariables>(
			DISCONNECT_CAREGIVER,
			{ refetchQueries: [GetUserInfoDocument] },
		);

		const { id: loggedUserId } = useLoggedUser();

		const { caregivers: careGivers } = careReceiverInfo;

		const handleItemPress = useCallback(
			(itemId: string) => () => {
				push('Profile', { userId: itemId });
			},
			[push],
		);

		const handleDeletePress = useCallback(
			(careGiver: GetUserInfoQuery['user']['caregivers'][number]) => async () => {
				const careGiverName = getName(careGiver);
				const careReceiverName = getName(careReceiverInfo);
				const confimation = await confirm(
					t('profile.delete-caregiver-confirm-title', { careGiverName }),
					t('profile.delete-caregiver-confirm-content', { careGiverName, careReceiverName }),
				);

				if (!confimation) {
					return;
				}
				await disconnectCareGiver({
					variables: {
						careReceiverUuid: careReceiverInfo.uuid,
						careGiverUuid: careGiver.uuid,
					},
				});
			},
			[careReceiverInfo, confirm, disconnectCareGiver, t],
		);

		return (
			<View style={style} {...props}>
				<Text category={Platform.select({ web: 'h5', default: 'h4' })} style={styles.marginBottom}>
					{t('profile.connections')}
				</Text>
				{careGivers.map((careGiver, index) => (
					<Fragment key={careGiver.id}>
						<Connection
							careGiver={careGiver}
							key={careGiver.id}
							onPress={handleItemPress(careGiver.id)}
							onDeletePress={
								enableDeleteCareGivers && loggedUserId !== careGiver.id
									? handleDeletePress(careGiver)
									: undefined
							}
						/>
						{Platform.OS !== 'web' && index !== careGivers.length - 1 && <Divider />}
					</Fragment>
				))}
			</View>
		);
	},
);

const Connection = ({
	careGiver,
	onPress,
	onDeletePress,
}: {
	careGiver: GetUserInfoQuery['user']['guardians'][number];
	onPress: () => unknown;
	onDeletePress?: () => unknown;
}) => {
	return (
		<TouchableOpacity onPress={onPress} style={styles.item}>
			<UserInfo
				bolded
				id={careGiver.id}
				name={getName(careGiver)}
				email={careGiver.username}
				photoUrl={careGiver.photoUrl || ''}
				onPress={onPress}
			/>
			{onDeletePress ? (
				<TouchableOpacity onPress={onDeletePress}>
					<DeleteIcon />
				</TouchableOpacity>
			) : (
				<ArrowRightIcon />
			)}
		</TouchableOpacity>
	);
};

const styles = StyleSheet.create({
	item: {
		flex: 1,
		flexDirection: 'row',
		paddingVertical: 12,
	},
	marginBottom: Platform.select({ web: { marginBottom: spacing(3) }, default: {} }),
});

interface Props extends ViewProps {
	careReceiverInfo: GetUserInfoQuery['user'];
	enableDeleteCareGivers?: boolean;
}
